import { useMemo, useState } from 'react';
import { History, Pencil, Plus, X } from 'lucide-react';
import { toast } from 'sonner@2.0.3';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { api } from '../../../shared/api/client';
import { useCachedQuery } from '../../../shared/lib/queryCache';
import { GROUPS_KEY, publishGroupsApi } from '../api';
import {
  groupMembers, groupState, sameSet, toggleGroup, upsertGroup, type AccountGroup,
} from '../lib/groups';

/** Группы пабликов человека: общие для сайта и приложения. */
export function useAccountGroups() {
  const query = useCachedQuery<{ groups: AccountGroup[] }>(
    GROUPS_KEY, () => api.get<{ groups: AccountGroup[] }>('/montage/account-groups'),
  );
  return query.data?.groups ?? [];
}

/**
 * Строка над списком пабликов: «Как в прошлый раз» и свои группы
 * («Русские», «Казахские»). Нажатие добавляет паблики группы к отмеченным,
 * повторное — снимает. Отмеченное можно сохранить новой группой.
 */
export function AccountGroupsBar({
  availableIds, checked, onChange, lastIds,
}: {
  /** Паблики, которые в этой форме можно отметить. */
  availableIds: readonly string[];
  checked: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** Набор прошлой публикации; пусто — кнопки нет. */
  lastIds: readonly string[];
}) {
  const groups = useAccountGroups();
  const available = useMemo(() => new Set(availableIds), [availableIds]);
  const last = lastIds.filter((id) => available.has(id));
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (next: AccountGroup[], done: string) => {
    setBusy(true);
    try {
      await publishGroupsApi.saveGroups(next);
      toast.success(done);
      return true;
    } catch (e) {
      toast.error(`Группу не сохранили: ${e instanceof Error ? e.message : 'ошибка'}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const clean = name.trim();
    if (!clean) return;
    const replaced = groups.some((g) => g.name.toLowerCase() === clean.toLowerCase());
    if (await save(upsertGroup(groups, clean, checked), replaced ? `Группа «${clean}» обновлена` : `Группа «${clean}» сохранена`)) {
      setNaming(false);
      setName('');
    }
  };

  const remove = (group: AccountGroup) => save(groups.filter((g) => g.id !== group.id), `Группа «${group.name}» удалена`);

  if (!groups.length && !last.length && checked.size < 2) return null;

  return (
    <div className="mb-3 space-y-2" data-testid="account-groups">
      <div className="flex flex-wrap items-center gap-2">
        {last.length > 0 && !sameSet(checked, last) && !editing && (
          <Badge variant="outline" className="cursor-pointer gap-1" onClick={() => onChange(new Set(last))}>
            <History className="size-3" /> Как в прошлый раз · {last.length}
          </Badge>
        )}
        {groups.map((g) => {
          const state = groupState(g, checked, available);
          const count = groupMembers(g, available).length;
          if (!count && !editing) return null;
          return (
            <Badge
              key={g.id}
              variant={state === 'all' ? 'default' : state === 'some' ? 'secondary' : 'outline'}
              className="cursor-pointer gap-1"
              title={editing ? 'Удалить группу' : state === 'all' ? 'Снять паблики группы' : 'Отметить паблики группы'}
              onClick={() => (editing ? void remove(g) : onChange(toggleGroup(g, checked, available)))}
            >
              {g.name} · {count}
              {editing && <X className="size-3" />}
            </Badge>
          );
        })}
        {!naming && !editing && checked.size >= 2 && (
          <button type="button" onClick={() => setNaming(true)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <Plus className="size-3" /> Сохранить отмеченные как группу
          </button>
        )}
        {groups.length > 0 && !naming && (
          <button type="button" onClick={() => setEditing((v) => !v)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            {editing ? 'Готово' : <><Pencil className="size-3" /> Группы</>}
          </button>
        )}
      </div>
      {naming && (
        <div className="flex gap-2">
          <Input
            autoFocus
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void create(); } }}
            placeholder={`Название, например «Казахские» (${checked.size})`}
            className="h-9"
          />
          <Button size="sm" disabled={busy || !name.trim()} onClick={() => void create()}>Сохранить</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setNaming(false); setName(''); }}>Отмена</Button>
        </div>
      )}
    </div>
  );
}
